import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { Box, Button, Card, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { Link } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";
import { tenantService } from "@/services/breeze/TenantService";
import { useRoles } from "@/hooks/useCommonQueries";
import { useAuth } from "@/providers/AuthProvider";
import CheckboxField from "@/components/form/CheckboxField";
import SelectField from "@/components/form/SelectField";
import { UpdateTenantRequest } from "@/types/breeze/Tenant";
import toast from "react-hot-toast";

type FormShape = {
  allow_registrations: boolean;
  require_email_confirmation: boolean;
  default_role_id: number;
};

export default function UserAccessSettings() {
  const queryClient = useQueryClient();
  const { currentTenant, isTenantAdmin } = useAuth();
  const { data: roles } = useRoles();

  const { data: tenant } = useQuery({
    queryKey: ["tenant", currentTenant?.id],
    queryFn: () => tenantService.getTenantById(currentTenant!.id),
    enabled: !!currentTenant?.id && isTenantAdmin,
  });

  const { mutate: updateTenant, isPending } = useMutation({
    mutationFn: (data: UpdateTenantRequest) =>
      tenantService.updateTenant(currentTenant!.id, data),
    onSuccess: () => {
      toast.success("User access settings saved");
      queryClient.invalidateQueries({ queryKey: ["tenant", currentTenant?.id] });
    },
  });

  if (!isTenantAdmin || !tenant) return null;

  const initialValues: FormShape = {
    allow_registrations: tenant.tenant_settings?.allow_registrations || false,
    require_email_confirmation: tenant.require_email_confirmation || false,
    default_role_id: tenant.tenant_settings?.default_role_id || 0,
  };

  return (
    <Card size="3" mb="6">
      <Box p="1">
        <Heading size="4" weight="medium" mb="1">
          User access
        </Heading>
        <Text size="2" color="gray" as="p" mb="4">
          Controls for how new users join this organisation and what they get
          when they first arrive.
        </Text>
        <Formik<FormShape>
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values) => {
            updateTenant({
              tenant_settings: {
                ...tenant.tenant_settings,
                allow_registrations: values.allow_registrations,
                default_role_id: values.default_role_id,
              },
              require_email_confirmation: values.require_email_confirmation,
            } as UpdateTenantRequest);
          }}
        >
          {({ values }) => (
            <Form>
              <Grid columns={{ initial: "1", md: "2" }} gap="6">
                <Flex direction="column" gap="3">
                  <Heading size="2" color="gray" weight="medium" mb="1">
                    Sign up
                  </Heading>
                  <CheckboxField
                    name="allow_registrations"
                    label="Allow registrations"
                  />
                  <CheckboxField
                    name="require_email_confirmation"
                    label="Require email confirmation for new users"
                  />
                  {values.allow_registrations && (
                    <SelectField
                      name="default_role_id"
                      label="Default role for new users"
                      type="number"
                      required
                    >
                      {roles
                        ?.filter((r) => r.is_administrator === false)
                        .map((role) => (
                          <SelectField.Item key={role.id} value={role.id}>
                            {role.name}
                          </SelectField.Item>
                        ))}
                    </SelectField>
                  )}
                </Flex>

                <Flex direction="column" gap="3">
                  <Heading size="2" color="gray" weight="medium" mb="1">
                    Free trial configuration
                  </Heading>
                  <Card variant="surface">
                    <Flex direction="column" gap="3" p="2">
                      <Flex align="center" gap="2">
                        <Gift size={16} />
                        <Text size="2" weight="medium">
                          Give new users a head-start
                        </Text>
                      </Flex>
                      <Text size="2" color="gray">
                        Offer every signed-up user a free window of full app
                        access before they need to subscribe. Set the length,
                        eligibility, and rewards for inviting friends.
                      </Text>
                      <Link
                        to={`/org/${currentTenant?.id}/growth?tab=engagement`}
                        style={{
                          color: "var(--accent-11)",
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        Configure free trials
                        <ArrowRight size={14} />
                      </Link>
                    </Flex>
                  </Card>
                </Flex>
              </Grid>
              <Flex justify="end" mt="5">
                <Button type="submit" loading={isPending}>
                  Save
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Box>
    </Card>
  );
}
